import { defer } from 'react-router-dom';

import storeApp from 'store/storeApp';

import {
  getPostFlowUrl,
  getReturnUrlFromRequestLoader,
} from 'utils/common/route';
import { getData } from 'utils/storage';
import { checkUserToken } from 'utils/author';
import { ensureUserInfoLoaded } from 'utils/loaderUtils';

import { getWalmartAuthorizationParams } from 'hooks/router';
import { queryBrandingNavigation } from 'hooks/useGetBranding';

import { ensureRenewalNotificationData } from 'pages/renewal-notification/useRenewalNotificationQuery';

const getUserId = (userInfo) => {
  return userInfo?.id;
};

export const renewalNotificationLoader =
  (queryClient) =>
  async ({ request }) => {
    const returnUrl = getReturnUrlFromRequestLoader(request);
    const { walmartSearchQuery } = getWalmartAuthorizationParams(request);

    const state = storeApp.getState();
    const dispatch = storeApp.dispatch;

    let userInfo = state?.user?.userInfo;

    const isUserLoggedIn = checkUserToken();
    const impersonateData = getData('impersonate');

    let renewalNotificationData = null;
    let renewalNotificationError = null;

    // Get EULA for authorized users
    if (isUserLoggedIn) {
      try {
        if (!userInfo) {
          try {
            const { userInfo: loadedUserInfo } = await ensureUserInfoLoaded({
              queryClient,
              returnUrl,
            });
            userInfo = loadedUserInfo;
          } catch (error) {
            console.log('error: ', error);
          }
        }

        const userId = getUserId(userInfo);
        const renewalNotificationQueryData =
          await ensureRenewalNotificationData(queryClient, userId);

        if (!renewalNotificationQueryData) {
          renewalNotificationError = 'not-exist';
        } else {
          const isAccepted = renewalNotificationQueryData?.isAccepted;

          renewalNotificationData = {
            renewalNotification: renewalNotificationQueryData,
            renewalNotificationContent:
              renewalNotificationQueryData.renewalNotificationContent,
            type: 'authorized',
          };

          // ✅ Calculate redirect URL for component to use
          const redirectUrl = getPostFlowUrl({
            type: 'renewal-notification',
            returnUrl,
            dispatch,
          });

          if (isAccepted) {
            // ✅ EULA is accepted - defer branding data loading
            const brandingPromise = queryBrandingNavigation({
              queryClient,
              userId,
            })
              .then((brandingData) => {
                return {
                  brandingData,
                  shouldNavigate: true,
                  navigationUrl: redirectUrl,
                  completed: true,
                };
              })
              .catch((error) => {
                return {
                  brandingData: null,
                  shouldNavigate: true,
                  navigationUrl: redirectUrl,
                  completed: true,
                  error: error.message,
                };
              });

            // ✅ Return deferred data - EULA component handles navigation
            return defer({
              renewalNotificationData,
              renewalNotificationContent:
                renewalNotificationData?.renewalNotificationContent,
              renewalNotificationError,
              returnUrl,
              walmartSearchQuery,
              userInfo,
              impersonateData,
              isUserLoggedIn,
              isAccepted: true,
              isLoading: true,
              brandingPromise,
              redirectUrl,
            });
          } else {
            // ✅ EULA not accepted - defer branding data for form display
            const brandingPromise = queryBrandingNavigation({
              queryClient,
              userId,
            })
              .then((brandingData) => {
                return {
                  brandingData,
                  shouldNavigate: false,
                  navigationUrl: null,
                  completed: true,
                };
              })
              .catch((error) => {
                return {
                  brandingData: null,
                  shouldNavigate: false,
                  navigationUrl: null,
                  completed: true,
                  error: error.message,
                };
              });

            // ✅ Return deferred data - EULA component shows form
            return defer({
              renewalNotificationData,
              renewalNotificationContent:
                renewalNotificationData?.renewalNotificationContent,
              renewalNotificationError,
              returnUrl,
              walmartSearchQuery,
              userInfo,
              impersonateData,
              isUserLoggedIn,
              isAccepted: false,
              isLoading: true,
              brandingPromise,
              redirectUrl: null, // ✅ No redirect URL for non-accepted
            });
          }
        }
      } catch (error) {
        if (error?.status === 302 || error instanceof Response) {
          throw error;
        }
        renewalNotificationError = 'not-exist';
      }
    }

    // Return fallback data for errors
    return {
      renewalNotificationData,
      renewalNotificationContent:
        renewalNotificationData?.renewalNotificationContent,
      renewalNotificationError,
      returnUrl,
      walmartSearchQuery,
      userInfo,
      impersonateData,
      isUserLoggedIn,
      isAccepted:
        renewalNotificationData?.renewalNotification?.isAccepted || false,
      isLoading: false,
      brandingPromise: null,
      redirectUrl: null,
    };
  };
